// src/service/AIService.js
//
// Turns a spoken sentence into the fields of an expense, using Gemini.
//
// The call goes straight from the browser with REACT_APP_GEMINI_API_KEY, which
// CRA inlines into the bundle: the key is readable by anyone who opens the site.
// Restrict it in the Google Cloud console (Generative Language API only, this
// site's domains as HTTP referrers, a daily quota) rather than relying on it
// being unnoticed.

import { toLocalYMD } from "../utils/localDate";

// One switch for the whole feature: without REACT_APP_AI=true the + goes
// straight to the form, exactly as it did before any of this existed.
const AI_ENABLED = String(process.env.REACT_APP_AI || "").trim().toLowerCase() === "true";

// Who sees it, once it is on: "ALL", or a comma separated list of emails.
// Left empty it means everyone — REACT_APP_AI is the deliberate switch, this
// only narrows the audience.
const AI_AUDIENCE = String(process.env.REACT_APP_AI_USER || "").trim();
const AI_FOR_EVERYONE = AI_AUDIENCE === "" || AI_AUDIENCE.toUpperCase() === "ALL";
const AI_ALLOWED = new Set(
  AI_AUDIENCE.split(",")
    .map((entry) => entry.trim().toLowerCase())
    .filter(Boolean)
);

const GEMINI_API_KEY = (process.env.REACT_APP_GEMINI_API_KEY || "").trim();
const GEMINI_MODEL = process.env.REACT_APP_GEMINI_MODEL || "gemini-3.8-flash";
const GEMINI_URL = `https://generativelanguage.googleapis.com/v1beta/models/${GEMINI_MODEL}:generateContent`;

// a sentence long enough to hold several expenses, short enough to bound cost
const MAX_TRANSCRIPT = 400;

/**
 * Whether the voice entry should be offered at all, to this person.
 * Reads the signed-in email itself so callers cannot show it to the wrong user
 * from a stale closure.
 */
export const isAIConfigured = (email) => {
  if (!AI_ENABLED || !GEMINI_API_KEY) return false;
  if (AI_FOR_EVERYONE) return true;

  let who = String(email || "").trim().toLowerCase();
  if (!who) {
    try {
      who = String(localStorage.getItem("loggedInUserEmail") || "").trim().toLowerCase();
    } catch {
      return false;
    }
  }
  return !!who && AI_ALLOWED.has(who);
};

/**
 * The shape Gemini must answer in. `category` is an enum of the categories this
 * user actually has, so it cannot invent one, and every field the model is
 * allowed to leave out is listed in `missing` instead of being guessed.
 */
const buildSchema = (categories) => ({
  type: "OBJECT",
  properties: {
    items: {
      type: "ARRAY",
      items: {
        type: "OBJECT",
        properties: {
          amount: { type: "NUMBER", nullable: true },
          category: { type: "STRING", enum: categories, nullable: true },
          description: { type: "STRING", nullable: true },
          date: { type: "STRING", nullable: true },
          scope: { type: "STRING", enum: ["personal", "shared"] },
          confidence: { type: "NUMBER" },
          missing: { type: "ARRAY", items: { type: "STRING" } },
        },
        required: ["scope", "confidence", "missing"],
      },
    },
    note: { type: "STRING", nullable: true },
  },
  required: ["items"],
});

const buildPrompt = ({ transcript, categories, currency, todayLocal, scope }) => `
You turn one spoken sentence into expense records for a personal expense tracker.

Today is ${todayLocal} in the speaker's own timezone. Their currency symbol is ${currency}.
The screen they spoke from records ${scope} expenses.

Their categories, and the only values "category" may take:
${categories.join(", ")}

Rules:
- Never invent an amount. If no number was spoken, set "amount" to null and put "amount" in "missing".
- "description" is the shop, person or thing the money went to, in the speaker's own words
  ("Kroger", "coffee", "school fees"). Title Case it. Never repeat the category as the description.
- Pick the closest category from the list. If nothing is close, use OTHER when it exists,
  else the first category, and add "category" to "missing".
- Resolve spoken dates against today: "yesterday", "last Friday", "on the 3rd". Answer as YYYY-MM-DD.
  With no date spoken, use ${todayLocal}.
- "scope" is "shared" only when the speaker says so (shared, family, joint, our, with <name>).
  Otherwise "${scope}".
- One sentence can hold several expenses ("50 at Kroger and 20 for fuel"): one item each.
- "confidence" is 0 to 1: how sure you are of the amount and category together.
- Amounts are plain numbers: "fifty dollars" is 50, "1,250.50" is 1250.5.

The sentence:
"""
${transcript}
"""
`.trim();

/**
 * @returns {Promise<{items: Array, note: string|null}>}
 * @throws  {Error} with a message worth showing when the call cannot be made
 */
export const parseExpenseFromSpeech = async ({
  transcript,
  categories = [],
  currency = "$",
  scope = "personal",
  todayLocal = toLocalYMD(),
}) => {
  if (!isAIConfigured()) {
    throw new Error(AI_ENABLED ? "Voice entry is not set up on this build." : "Voice entry is switched off.");
  }

  const said = (transcript || "").trim().slice(0, MAX_TRANSCRIPT);
  if (!said) {
    throw new Error("I did not catch that.");
  }

  const original = categories.length ? categories.map(String) : ["OTHER"];
  const cats = original.map((c) => c.toUpperCase());
  // GROCERIES -> Groceries: the picker's options are the user's own spellings
  const asUserWroteIt = original.reduce((map, name) => ({ ...map, [name.toUpperCase()]: name }), {});

  const res = await fetch(GEMINI_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-goog-api-key": GEMINI_API_KEY,
    },
    body: JSON.stringify({
      contents: [
        {
          role: "user",
          parts: [{ text: buildPrompt({ transcript: said, categories: cats, currency, todayLocal, scope }) }],
        },
      ],
      generationConfig: {
        temperature: 0,
        maxOutputTokens: 1024,
        responseMimeType: "application/json",
        responseSchema: buildSchema(cats),
        // 2.5 Flash spends output tokens thinking, which truncated longer
        // sentences mid-JSON. Extraction this narrow does not need it, and
        // without it a parse takes about a second instead of three.
        thinkingConfig: { thinkingBudget: 0 },
      },
    }),
  });

  if (!res.ok) {
    let errMessage = "";
    try {
      const errJson = await res.json();
      errMessage = errJson?.error?.message || "";
    } catch {
      errMessage = await res.text().catch(() => "");
    }
    console.error("Gemini error", res.status, errMessage);
    if (res.status === 429) {
      throw new Error("Too many tries just now — give it a moment.");
    }
    if (res.status === 401 || res.status === 403) {
      throw new Error(
        errMessage
          ? `Gemini API key error: ${errMessage}`
          : "Gemini API key is invalid or revoked. Please update your API key."
      );
    }
    throw new Error("Could not read that back. Try again, or type it.");
  }

  const data = await res.json();
  const candidate = data?.candidates?.[0];
  const text = candidate?.content?.parts?.map((p) => p.text).join("") || "";

  if (candidate?.finishReason && candidate.finishReason !== "STOP") {
    console.error("Gemini stopped early:", candidate.finishReason);
    throw new Error("That was a long one — try saying it in a shorter sentence.");
  }

  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch {
    console.error("Gemini returned non-JSON:", text);
    throw new Error("Could not read that back. Try again, or type it.");
  }

  const items = (parsed?.items || [])
    .map((item) => normaliseItem(item, { cats, asUserWroteIt, todayLocal, scope }))
    .filter(Boolean);

  if (!items.length) {
    throw new Error("I could not find an expense in that.");
  }

  return { items, note: parsed?.note || null };
};

// Never trust the model's own bookkeeping: re-derive what is missing here.
const normaliseItem = (item, { cats, asUserWroteIt, todayLocal, scope }) => {
  if (!item) return null;

  const amount = Number(item.amount);
  const hasAmount = Number.isFinite(amount) && amount > 0;

  const spokenCategory = (item.category || "").toUpperCase();
  const category = cats.includes(spokenCategory) ? spokenCategory : cats.includes("OTHER") ? "OTHER" : cats[0];

  const date = /^\d{4}-\d{2}-\d{2}$/.test(item.date || "") ? item.date : todayLocal;

  // The enum means the model always answers with a real category, so its own
  // admission that nothing fitted is the only way to know it guessed.
  const modelSaid = Array.isArray(item.missing) ? item.missing : [];

  const missing = [];
  if (!hasAmount) missing.push("amount");
  if (!spokenCategory || spokenCategory !== category || modelSaid.includes("category")) missing.push("category");
  if (!item.description || modelSaid.includes("description")) missing.push("description");

  return {
    amount: hasAmount ? Math.round(amount * 100) / 100 : "",
    category: asUserWroteIt[category] || category,
    description: (item.description || "").trim(),
    date,
    scope: item.scope === "shared" || item.scope === "personal" ? item.scope : scope,
    confidence: Number.isFinite(Number(item.confidence)) ? Number(item.confidence) : 0,
    missing,
  };
};

export default parseExpenseFromSpeech;
